import { Button, Tooltip } from "@material-ui/core";
import React, { Fragment, useCallback } from "react";
import {
  Datagrid,
  Filter,
  List,
  TextField,
  SearchInput,
  FunctionField,
  ListProps,
  ReferenceField,
  DateInput,
  TopToolbar,
  Button as DefaultButton,
  sanitizeListRestProps,
  useDataProvider,
  useListContext,
  SelectInput,
  useNotify,
} from "react-admin";
import IconAssess from "@material-ui/icons/Assessment";
import { Helmet } from "react-helmet";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ListAltIcon from "@material-ui/icons/ListAlt";
import StatusField from "components/StatusField";
import CustomDateField from "components/CustomDateField";
import BulkActionButton from "components/BulkActionButton";
import moment from "moment";
import { SchoolIdSelected } from "utils/tools";
import CustomPagination from "components/CustomPagination";
import _ from "lodash";
import haversine from "haversine";
import { Parser } from "json2csv";
import fileDownload from "js-file-download";
import DriverWithPhoneField from "./components/DriverWithPhoneField";
import BusScheduleByBusSchedulePlanInput from "pages/notTakingReports/components/BusScheduleByBusSchedulePlanInput";
import PassengerReportButton from "./components/PassengerReportButton";

interface Area {
  name: string;
  minRange: number;
  maxRange: number;
  passengers: Passenger[];
}

interface Passenger {
  id: number;
  logs: any[];
  student: {
    firstName: string;
    id: number;
    lastName: string;
    level: any;
    levelGroup: any;
    levelId: number;
    photoUrl: string;
  };
}

const AssignmentFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
    <DateInput source="EstimatedStartedAt" label="Date" alwaysOn />
    <SelectInput
      source="Status"
      alwaysOn
      choices={[
        { id: "Ready", name: "Ready" },
        { id: "Started", name: "Started" },
        { id: "Ended", name: "Ended" },
        { id: "ForceEnded", name: "Force Ended" },
        { id: "EndedByAdmin", name: "Ended By Admin" },
        { id: "Cancelled", name: "Cancelled" },
        { id: "Expired", name: "Expired" },
      ]}
    />
    <BusScheduleByBusSchedulePlanInput
      source="BusSchedulePlanId"
      schoolId={SchoolIdSelected()}
      label="Wave"
      allowEmpty
      alwaysOn
    />
  </Filter>
);

const BulkActionButtons = (props: any) => (
  <Fragment>
    {/* <BulkActionButton
      action="draft"
      label="Set Draft"
      resource="assignments"
      {...props}
    /> */}
    <BulkActionButton
      action="ready"
      label="Set Ready"
      resource="assignments"
      {...props}
    />
    <BulkActionButton
      action="cancelled"
      label="Set Cancel"
      resource="assignments"
      {...props}
    />
    <BulkActionButton
      action="started"
      label="Set Start"
      resource="assignments"
      {...props}
    />
    <BulkActionButton
      action="ended"
      label="Set End"
      resource="assignments"
      {...props}
    />

    {/* <BulkActionButton
      action="delete"
      label="DELETE"
      resource="assignments"
      {...props}
    /> */}
  </Fragment>
);

const ListActions = (props: any) => {
  const {
    className,
    exporter,
    filters,
    maxResults,
    history,
    setShowModal,
    ...rest
  } = props;

  const listCtx = useListContext();
  const notify = useNotify();

  const { getList, customGetList } = useDataProvider();

  const downloadCSV = useCallback(async () => {
    const areaRes = await getList("areas", {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "", order: "" },
      filter: {
        SchoolId: listCtx.filter.SchoolId,
      },
    });

    const res = await getList("assignments", {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "", order: "" },
      filter: {
        EstimatedStartedAt: listCtx.filterValues.EstimatedStartedAt,
        SchoolId: listCtx.filter.SchoolId,
      },
    });

    let areaDefinition: Area[] = [];

    //if school is not TWS
    if (listCtx.filter.SchoolId !== 5) {
      areaRes.data.forEach((item, index) => {
        if (index === 0) {
          areaDefinition.push({
            name: item.name,
            minRange: 0,
            maxRange: item.maxDistanceInMeters,
            passengers: [],
          });
        } else {
          areaDefinition.push({
            name: item.name,
            minRange: areaDefinition[index - 1].maxRange,
            maxRange: item.maxDistanceInMeters,
            passengers: [],
          });
        }
      });

      Promise.all(
        res.data.map((assignment: any) => {
          const areaData = _.cloneDeep(areaDefinition);

          const schoolLocation = {
            latitude: assignment.school.lat,
            longitude: assignment.school.lng,
          };

          assignment.locations.forEach((location: any) => {
            const distance = haversine(
              schoolLocation,
              {
                latitude: location.lat,
                longitude: location.lng,
              },
              { unit: "meter" }
            );

            areaData.forEach((area) => {
              if (distance >= area.minRange && distance <= area.maxRange) {
                area.passengers = [...area.passengers, ...location.passengers];
              }
            });
          });

          const csvData: any = {
            "Route Name": assignment.routeName,
          };

          areaData.forEach((area: any) => {
            csvData[area.name] = area.passengers.length;
          });

          return csvData;
        })
      ).then((result) => {
        const parser = new Parser();

        fileDownload(
          parser.parse(result),
          `Area Report ${res.data[0] ? res.data[0].school.name : ""} ${
            listCtx.filterValues.EstimatedStartedAt
          }.csv`
        );
      });
    } else {
      const subConAreaDefinitions: Area[] = [];

      for (let iteration = 0; iteration < 28; iteration++) {
        if (iteration === 0) {
          subConAreaDefinitions.push({
            name: `SubCon Area ${iteration + 1} (0km - ${
              (iteration + 1) * 2
            }km)`,
            minRange: 0,
            maxRange: (iteration + 1) * 2 * 1000,
            passengers: [],
          });
        } else {
          subConAreaDefinitions.push({
            name: `SubCon Area ${iteration + 1} (${
              subConAreaDefinitions[iteration - 1]?.maxRange
                ? subConAreaDefinitions[iteration - 1].maxRange / 1000
                : 0
            }km  - ${(iteration + 1) * 2}km)`,
            minRange: subConAreaDefinitions[iteration - 1].maxRange,
            maxRange: (iteration + 1) * 2 * 1000,
            passengers: [],
          });
        }
      }

      areaRes.data.forEach((item, index) => {
        if (index === 0) {
          areaDefinition.push({
            name: `${item.name} (0km - ${item.maxDistanceInMeters / 1000}km)`,
            minRange: 0,
            maxRange: item.maxDistanceInMeters,
            passengers: [],
          });
        } else {
          areaDefinition.push({
            name: `${item.name} (${
              areaDefinition[index - 1]?.maxRange
                ? areaDefinition[index - 1].maxRange / 1000
                : 0
            }km - ${item.maxDistanceInMeters / 1000}km)`,
            minRange: areaDefinition[index - 1].maxRange,
            maxRange: item.maxDistanceInMeters,
            passengers: [],
          });
        }
      });

      Promise.all(
        res.data.map((assignment: any) => {
          const areaData = _.cloneDeep(areaDefinition);
          const subConAreaData = _.cloneDeep(subConAreaDefinitions);

          const schoolLocation = {
            latitude: assignment.school.lat,
            longitude: assignment.school.lng,
          };

          assignment.locations.forEach((location: any) => {
            const distance = haversine(
              schoolLocation,
              {
                latitude: location.lat,
                longitude: location.lng,
              },
              { unit: "meter" }
            );

            areaData.forEach((area) => {
              if (distance >= area.minRange && distance <= area.maxRange) {
                area.passengers = [...area.passengers, ...location.passengers];
              }
            });

            subConAreaData.forEach((area) => {
              if (distance >= area.minRange && distance <= area.maxRange) {
                area.passengers = [...area.passengers, ...location.passengers];
              }
            });
          });

          const csvData: any = {
            "Route Name": assignment.routeName,
          };

          areaData.forEach((area: any) => {
            csvData[area.name] = area.passengers.length;
          });

          subConAreaData.forEach((area: any) => {
            csvData[area.name] = area.passengers.length;
          });

          return csvData;
        })
      ).then((result) => {
        const parser = new Parser();

        fileDownload(
          parser.parse(result),
          `Area Report ${res.data[0] ? res.data[0].school.name : ""} ${
            listCtx.filterValues.EstimatedStartedAt
          }.csv`
        );
      });
    }
  }, [listCtx, getList]);

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      <DefaultButton onClick={downloadCSV}>
        <>
          <IconAssess />
          <span>Download Area Report</span>
        </>
      </DefaultButton>
      <PassengerReportButton
        customGetList={customGetList}
        listCtx={listCtx}
        notify={notify}
      />
    </TopToolbar>
  );
};

const AssignmentList = (props: ListProps) => {
  const AssignmentShowButton = (record: IAssignment) => {
    return (
      <Tooltip title="Show Assignment Detail">
        <Button href={`/#/assignments/${record.id}/show`}>
          <VisibilityIcon />
        </Button>
      </Tooltip>
    );
  };

  const TimesheetShowButton = (record: IAssignment) => {
    return (
      <Tooltip title="Show Timesheets">
        <Button
          href={`/#/subcontractorTimesheets?filter=${JSON.stringify({
            AssignmentId: record.id,
            Date: moment().format("Y-MM-DD"),
          })}`}
        >
          <ListAltIcon />
        </Button>
      </Tooltip>
    );
  };

  return (
    <>
      <Helmet>
        <title>Bus Services</title>
      </Helmet>
      <List
        exporter={false}
        hasCreate={false}
        filters={<AssignmentFilter />}
        filterDefaultValues={{
          EstimatedStartedAt: `${moment().format("Y-MM-DD")}`,
        }}
        filter={{
          SchoolId: Number(SchoolIdSelected()),
        }}
        bulkActionButtons={<BulkActionButtons />}
        actions={<ListActions />}
        title={"Bus Services"}
        perPage={50}
        sort={{ field: "routeName", order: "ASC" }}
        {...props}
        pagination={<CustomPagination />}
      >
        <Datagrid>
          <TextField source="routeName" emptyText="-" />
          <ReferenceField
            source="vehicleId"
            label="Vehicle"
            reference="vehicles"
            emptyText="-"
            linkType="show"
          >
            <TextField source="plateNumber" />
          </ReferenceField>
          <ReferenceField
            source="driverId"
            label="Driver"
            reference="drivers"
            emptyText="-"
            linkType="show"
          >
            <DriverWithPhoneField />
          </ReferenceField>
          <ReferenceField
            source="busAssistantId"
            label="Bus Assistant"
            reference="busAssistants"
            emptyText="-"
            linkType="show"
          >
            <DriverWithPhoneField />
          </ReferenceField>
          <CustomDateField
            source="startedAt"
            label="Started At"
            emptyText="-"
            isDateTime
          />
          <CustomDateField
            source="endedAt"
            label="Ended At"
            emptyText="-"
            isDateTime
          />
          <ReferenceField
            label="Route Plan Wave"
            source="busSchedulePlanId"
            reference="busSchedulePlans"
            link={false}
          >
            <ReferenceField
              source="busScheduleId"
              reference="busSchedules"
              link={false}
            >
              <TextField source="name" />
            </ReferenceField>
          </ReferenceField>
          <TextField
            source="driverAppVersionUsed"
            label="Driver App Version"
            emptyText="-"
          />
          <StatusField source="status" />
          <FunctionField
            label="Actions"
            render={(props: any) => (
              <>
                <AssignmentShowButton {...props} />
                <TimesheetShowButton {...props} />
              </>
            )}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default AssignmentList;
